import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup, signOut, onIdTokenChanged, updateProfile, User } from 'firebase/auth';
import { getDatabase, ref as dbRef, get, set, onValue, push, update, remove, runTransaction, query, orderByChild, limitToLast } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import React from 'react';
import { TranscriptionData } from '../components/Modals/TranscriptionModal';
import { API_BASE_URL, GOOGLE_ANALYTICS_MEASUREMENT_ID } from '../config';
import {
  clearBackendSession,
  isAccountWaitlistedError,
  isBackendSessionAccessDeniedError,
  syncBackendSession,
} from './backendSession';
import { trackAnalyticsEvent } from '../analytics/analyticsEvents';
import { devAuthUser } from './devAuth';

/** Fixed for the life of the page: a dev session never signs in or out. */
const DEV_USER = devAuthUser();

// Firebase configuration (same as in ef_captions)
const firebaseConfig = {
  apiKey: "AIzaSyCCax7H4GyBWRBs0jmE7faGRcVcOQN-pyI",
  authDomain: "auth.imabird.productions", // imabird-102e4.firebaseapp.com
  databaseURL: "https://imabird-102e4-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "imabird-102e4",
  storageBucket: "imabird-102e4.appspot.com",
  messagingSenderId: "702817359304",
  appId: "1:702817359304:web:d06f75b48fd1c3ccce5cfd",
  ...(GOOGLE_ANALYTICS_MEASUREMENT_ID
    ? { measurementId: GOOGLE_ANALYTICS_MEASUREMENT_ID }
    : {}),
};

// Initialize Firebase
export const firebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);
export const database = getDatabase(firebaseApp);
export const storage = getStorage(firebaseApp);
const AUTH_ERROR_STORAGE_KEY = 'imabird.auth.error';

const readPersistedAuthError = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const stored = window.sessionStorage.getItem(AUTH_ERROR_STORAGE_KEY);
  return stored && stored.trim() ? stored.trim() : null;
};

const persistAuthError = (message: string | null): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const normalized = String(message || '').trim();
  if (!normalized) {
    window.sessionStorage.removeItem(AUTH_ERROR_STORAGE_KEY);
    return;
  }
  window.sessionStorage.setItem(AUTH_ERROR_STORAGE_KEY, normalized);
};

/* A refused sign-in signs the user out, and that sign-out re-runs the auth
   listener; the waitlist answer is kept here so the listener can restore it. */
const AUTH_WAITLIST_STORAGE_KEY = 'imabird.auth.waitlisted';

const readPersistedWaitlisted = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.sessionStorage.getItem(AUTH_WAITLIST_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const persistWaitlisted = (waitlisted: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (waitlisted) {
      window.sessionStorage.setItem(AUTH_WAITLIST_STORAGE_KEY, '1');
    } else {
      window.sessionStorage.removeItem(AUTH_WAITLIST_STORAGE_KEY);
    }
  } catch {
    // Without storage the answer still shows for this sign-in attempt.
  }
};

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) {
    const message = error.message.trim();
    if (message) {
      return message;
    }
  }
  return fallback;
};

// Sanitize media ID for Firebase paths
const sanitizeMediaId = (mediaId: string): string => {
  return mediaId
    .replace(/[.#$[\]]/g, '_') // Replace forbidden characters with underscore
    .replace(/\s+/g, '_') // Replace spaces with underscore
    .replace(/[^\w\-_.]/g, '_') // Replace any other special characters
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
};

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Authentication functions
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onIdTokenChanged(auth, callback);
};

// Sign in with Google
export const signInWithGoogle = async (): Promise<User | null> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    console.log('Google sign-in successful:', result.user.email);
    trackAnalyticsEvent('login', { method: 'google' });
    return result.user;
  } catch (error: any) {
    console.error('Google sign-in error:', error);
    
    // Handle specific error codes
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in was cancelled');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('Pop-up was blocked by browser');
    } else if (error.code === 'auth/cancelled-popup-request') {
      throw new Error('Another sign-in popup is already open');
    } else {
      throw new Error('Failed to sign in with Google');
    }
  }
};

// Sign in with an email and password account
export const signInWithEmail = async (email: string, password: string): Promise<User> => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    trackAnalyticsEvent('login', { method: 'password' });
    return result.user;
  } catch (error: any) {
    if (['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found', 'auth/invalid-email'].includes(error?.code)) {
      throw new Error('Wrong email or password.');
    }
    if (error?.code === 'auth/too-many-requests') {
      throw new Error('Too many attempts. Try again later.');
    }
    throw new Error('Failed to sign in');
  }
};

// Sign out
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
    console.log('User signed out successfully');
  } catch (error) {
    console.error('Sign-out error:', error);
    throw new Error('Failed to sign out');
  }
};

// Get current user
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return auth.currentUser !== null;
};

// Get user profile data
export const getUserProfile = (user: User) => {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    creationTime: user.metadata.creationTime,
    lastSignInTime: user.metadata.lastSignInTime
  };
};

// Save user profile to database (optional - for storing additional user data)
export const saveUserProfile = async (user: User, additionalData?: any): Promise<void> => {
  try {
    const userRef = dbRef(database, `users/${user.uid}/profile`);
    let existing: any = {};
    try {
      const snap = await get(userRef);
      if (snap.exists()) existing = snap.val();
    } catch {}

    const mergedProfile = {
      ...existing,
      ...getUserProfile(user),
      ...additionalData,
      // Preserve plan if previously set; default to 'free' when absent
      plan: (additionalData && additionalData.plan) || existing.plan || 'free',
      lastLoginAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await set(userRef, mergedProfile);
    console.log('User profile saved:', user.uid);
  } catch (error) {
    console.error('Error saving user profile:', error);
    throw error;
  }
};

// Get user profile from database
export const getUserProfileFromDB = async (userId: string): Promise<any | null> => {
  try {
    const userRef = dbRef(database, `users/${userId}/profile`);
    const snapshot = await get(userRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user profile from database:', error);
    throw error;
  }
};

// Analytics: record login timestamp
export const recordUserLogin = async (user: User): Promise<void> => {
  try {
    const logRef = dbRef(database, `users/${user.uid}/analytics/loginActivity/${Date.now()}`);
    await set(logRef, { timestamp: new Date().toISOString(), email: user.email || null });
  } catch (error) {
    console.error('Error recording user login:', error);
  }
};

// Analytics: record token usage per request
export const recordTokenUsage = async (userId: string, conversationId: string, requestId: string, tokens: number): Promise<void> => {
  try {
    const ref = dbRef(database, `users/${userId}/analytics/tokenUsage/${conversationId}/${requestId}`);
    await set(ref, { tokens, at: new Date().toISOString() });
  } catch (error) {
    console.error('Error recording token usage:', error);
  }
};

export interface DashboardFeedback {
  id: string;
  text: string;
  userId: string;
  email: string | null;
  displayName: string | null;
  path: string | null;
  createdAt: string;
  updatedAt: string;
  source: 'dashboard' | 'contact';
}

export const saveFeedback = async (params: {
  text: string;
  userId: string;
  email?: string | null;
  displayName?: string | null;
  path?: string | null;
  source: DashboardFeedback['source'];
}): Promise<DashboardFeedback> => {
  const text = params.text.trim();
  if (!text) {
    throw new Error('Feedback cannot be empty');
  }

  const now = new Date().toISOString();
  const feedbackRef = push(dbRef(database, 'feedback'));
  const id = feedbackRef.key;

  if (!id) {
    throw new Error('Failed to create feedback id');
  }

  const feedback: DashboardFeedback = {
    id,
    text,
    userId: params.userId,
    email: params.email || null,
    displayName: params.displayName || null,
    path: params.path || null,
    createdAt: now,
    updatedAt: now,
    source: params.source,
  };

  await set(feedbackRef, feedback);
  return feedback;
};

export const saveDashboardFeedback = async (
  params: Omit<Parameters<typeof saveFeedback>[0], 'source'>
): Promise<DashboardFeedback> => saveFeedback({ ...params, source: 'dashboard' });

// Analytics: set project count snapshot
export const setProjectCount = async (userId: string, count: number): Promise<void> => {
  try {
    const ref = dbRef(database, `users/${userId}/analytics/snapshots`);
    const snap = await get(ref);
    const current = snap.exists() ? snap.val() : {};
    await set(ref, { ...current, projectCount: count, updatedAt: new Date().toISOString() });
  } catch (error) {
    console.error('Error setting project count snapshot:', error);
  }
};

// Analytics: set asset count snapshot
export const setAssetCount = async (userId: string, count: number): Promise<void> => {
  try {
    const ref = dbRef(database, `users/${userId}/analytics/snapshots`);
    const snap = await get(ref);
    const current = snap.exists() ? snap.val() : {};
    await set(ref, { ...current, assetCount: count, updatedAt: new Date().toISOString() });
  } catch (error) {
    console.error('Error setting asset count snapshot:', error);
  }
};

// Update user's display name in Auth and DB
export const updateUserDisplayName = async (user: User, displayName: string): Promise<void> => {
  try {
    await updateProfile(user, { displayName });
    await saveUserProfile(user, { displayName });
  } catch (error) {
    console.error('Error updating display name:', error);
    throw error;
  }
};

// Upload avatar to Firebase Storage, update user photoURL and DB profile
export const uploadUserAvatar = async (user: User, file: File | Blob): Promise<string> => {
  try {
    const extension = (file instanceof File && file.name.includes('.')) ? file.name.split('.').pop() : 'jpg';
    const path = `users/${user.uid}/profile/avatar-${Date.now()}.${extension}`;
    const ref = storageRef(storage, path);
    await uploadBytes(ref, file);
    const downloadURL = await getDownloadURL(ref);
    await updateProfile(user, { photoURL: downloadURL });
    await saveUserProfile(user, { photoURL: downloadURL, avatarPath: path });
    return downloadURL;
  } catch (error) {
    console.error('Error uploading avatar:', error);
    throw error;
  }
};

// Optionally remove existing avatar from Storage when given a stored path
export const deleteUserAvatarByPath = async (avatarPath: string): Promise<void> => {
  try {
    if (!avatarPath) return;
    const ref = storageRef(storage, avatarPath);
    await deleteObject(ref);
  } catch (error) {
    console.warn('Warning: failed to delete previous avatar:', error);
  }
};

// Update user plan flag in DB
export const updateUserPlan = async (
  userId: string,
  plan: 'free' | 'basic' | 'pro' | 'studio'
): Promise<void> => {
  try {
    const userRef = dbRef(database, `users/${userId}/profile`);
    const snap = await get(userRef);
    const existing = snap.exists() ? snap.val() : {};
    await set(userRef, { ...existing, plan, updatedAt: new Date().toISOString() });
  } catch (error) {
    console.error('Error updating user plan:', error);
    throw error;
  }
};

export type PublishingPlatform = 'instagram' | 'youtube';
export type PublishingScheduleStatus = 'scheduled' | 'published';
export type PublishingSchedulePrivacyStatus = 'private' | 'unlisted' | 'public';

export interface PublishingScheduleEntry {
  id: string;
  platform: PublishingPlatform;
  scheduledFor: string;
  filename: string;
  caption: string;
  title: string;
  description: string;
  tags: string[];
  privacyStatus: PublishingSchedulePrivacyStatus;
  madeForKids: boolean;
  notifySubscribers: boolean;
  status: PublishingScheduleStatus;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  publishedUrl: string | null;
}

export interface CreatePublishingScheduleEntryInput {
  platform: PublishingPlatform;
  scheduledFor: string;
  filename: string;
  caption?: string;
  title?: string;
  description?: string;
  tags?: string[];
  privacyStatus?: PublishingSchedulePrivacyStatus;
  madeForKids?: boolean;
  notifySubscribers?: boolean;
  status?: PublishingScheduleStatus;
  publishedAt?: string | null;
  publishedUrl?: string | null;
}

const publishingSchedulePath = (userId: string) => `users/${userId}/publishing/schedule`;

const normalizePublishingScheduleTags = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((tag): tag is string => typeof tag === 'string')
    .map((tag) => tag.trim())
    .filter(Boolean);
};

const normalizePublishingScheduleEntry = (
  id: string,
  value: unknown
): PublishingScheduleEntry | null => {
  if (!value || typeof value !== 'object') return null;

  const raw = value as Record<string, unknown>;
  const platform =
    raw.platform === 'instagram' || raw.platform === 'youtube' ? raw.platform : null;
  const scheduledFor = typeof raw.scheduledFor === 'string' ? raw.scheduledFor : '';
  const filename = typeof raw.filename === 'string' ? raw.filename.trim() : '';

  if (!platform || !scheduledFor || !filename) {
    return null;
  }

  const createdAt =
    typeof raw.createdAt === 'string' && raw.createdAt ? raw.createdAt : new Date().toISOString();

  return {
    id,
    platform,
    scheduledFor,
    filename,
    caption: typeof raw.caption === 'string' ? raw.caption : '',
    title: typeof raw.title === 'string' ? raw.title : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    tags: normalizePublishingScheduleTags(raw.tags),
    privacyStatus:
      raw.privacyStatus === 'public' || raw.privacyStatus === 'unlisted'
        ? raw.privacyStatus
        : 'private',
    madeForKids: Boolean(raw.madeForKids),
    notifySubscribers: Boolean(raw.notifySubscribers),
    status: raw.status === 'published' ? 'published' : 'scheduled',
    createdAt,
    updatedAt: typeof raw.updatedAt === 'string' && raw.updatedAt ? raw.updatedAt : createdAt,
    publishedAt: typeof raw.publishedAt === 'string' ? raw.publishedAt : null,
    publishedUrl: typeof raw.publishedUrl === 'string' ? raw.publishedUrl : null,
  };
};

const sanitizePublishingScheduleInput = (
  input: CreatePublishingScheduleEntryInput
): Omit<PublishingScheduleEntry, 'id'> => {
  const timestamp = new Date().toISOString();

  return {
    platform: input.platform,
    scheduledFor: input.scheduledFor,
    filename: input.filename.trim(),
    caption: (input.caption || '').trim(),
    title: (input.title || '').trim(),
    description: (input.description || '').trim(),
    tags: normalizePublishingScheduleTags(input.tags || []),
    privacyStatus: input.privacyStatus || 'private',
    madeForKids: Boolean(input.madeForKids),
    notifySubscribers: Boolean(input.notifySubscribers),
    status: input.status === 'published' ? 'published' : 'scheduled',
    createdAt: timestamp,
    updatedAt: timestamp,
    publishedAt: input.publishedAt || null,
    publishedUrl: input.publishedUrl || null,
  };
};

export const listPublishingSchedule = async (
  userId: string
): Promise<PublishingScheduleEntry[]> => {
  try {
    const scheduleRef = dbRef(database, publishingSchedulePath(userId));
    const snapshot = await get(scheduleRef);
    if (!snapshot.exists()) {
      return [];
    }

    const rawEntries = snapshot.val() as Record<string, unknown>;
    return Object.entries(rawEntries)
      .map(([id, value]) => normalizePublishingScheduleEntry(id, value))
      .filter((entry): entry is PublishingScheduleEntry => Boolean(entry))
      .sort(
        (left, right) =>
          new Date(left.scheduledFor).getTime() - new Date(right.scheduledFor).getTime()
      );
  } catch (error) {
    console.error('Error loading publishing schedule:', error);
    return [];
  }
};

export const createPublishingScheduleEntry = async (
  userId: string,
  input: CreatePublishingScheduleEntryInput
): Promise<PublishingScheduleEntry> => {
  const scheduleRef = dbRef(database, publishingSchedulePath(userId));
  const entryRef = push(scheduleRef);
  const normalized = sanitizePublishingScheduleInput(input);

  await set(entryRef, normalized);

  return {
    id: entryRef.key || `schedule-${Date.now()}`,
    ...normalized,
  };
};

export const updatePublishingScheduleEntry = async (
  userId: string,
  entryId: string,
  changes: Partial<Omit<PublishingScheduleEntry, 'id' | 'createdAt'>>
): Promise<void> => {
  const entryRef = dbRef(database, `${publishingSchedulePath(userId)}/${entryId}`);
  const snapshot = await get(entryRef);
  if (!snapshot.exists()) {
    throw new Error('Publishing schedule entry not found');
  }

  const current = normalizePublishingScheduleEntry(entryId, snapshot.val());
  if (!current) {
    throw new Error('Publishing schedule entry is invalid');
  }

  const next: Omit<PublishingScheduleEntry, 'id'> = {
    ...current,
    ...changes,
    filename: typeof changes.filename === 'string' ? changes.filename.trim() : current.filename,
    caption: typeof changes.caption === 'string' ? changes.caption.trim() : current.caption,
    title: typeof changes.title === 'string' ? changes.title.trim() : current.title,
    description:
      typeof changes.description === 'string'
        ? changes.description.trim()
        : current.description,
    tags: changes.tags ? normalizePublishingScheduleTags(changes.tags) : current.tags,
    updatedAt: new Date().toISOString(),
  };

  await set(entryRef, next);
};

export const deletePublishingScheduleEntry = async (
  userId: string,
  entryId: string
): Promise<void> => {
  await remove(dbRef(database, `${publishingSchedulePath(userId)}/${entryId}`));
};

// Forum types
export interface ForumThread {
  id: string;
  title: string;
  body: string;
  tag: 'question' | 'update' | 'feedback';
  authorId: string;
  authorName: string;
  authorPhotoURL?: string | null;
  createdAt: string;
  updatedAt: string;
  replyCount: number;
  lastReplyAt: string;
  score: number;
  upvotes: number;
  downvotes: number;
  sharedContent?: ForumSharedContent | null;
}

export interface ForumReply {
  id: string;
  threadId: string;
  body: string;
  authorId: string;
  authorName: string;
  authorPhotoURL?: string | null;
  createdAt: string;
  updatedAt: string;
  score: number;
  upvotes: number;
  downvotes: number;
}

const forumThreadPath = 'forum/threads';
const forumRepliesPath = (threadId: string) => `forum/replies/${threadId}`;
const forumThreadVotesPath = (threadId: string) => `forum/threadVotes/${threadId}`;
const forumUserThreadVotesPath = (userId: string) => `forum/userThreadVotes/${userId}`;
const forumReplyVotesPath = (threadId: string, replyId: string) => `forum/replyVotes/${threadId}/${replyId}`;
const forumUserReplyVotesPath = (userId: string, threadId: string) => `forum/userReplyVotes/${userId}/${threadId}`;

export type ForumVote = -1 | 0 | 1;

export interface ForumSharedContent {
  kind: 'link' | 'asset';
  url: string;
  title?: string;
  assetName?: string;
  assetType?: 'video' | 'image' | 'audio';
  thumbnailUrl?: string | null;
  duration?: number | null;
}

const normalizeForumVote = (value: unknown): ForumVote => {
  const parsed = Number(value);
  if (parsed === 1) return 1;
  if (parsed === -1) return -1;
  return 0;
};

const normalizeForumSharedContent = (value: unknown): ForumSharedContent | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const kind = raw.kind === 'link' || raw.kind === 'asset' ? raw.kind : null;
  const url = typeof raw.url === 'string' ? raw.url.trim() : '';
  if (!kind || !url) return null;

  const assetType =
    raw.assetType === 'video' || raw.assetType === 'image' || raw.assetType === 'audio'
      ? raw.assetType
      : undefined;

  return {
    kind,
    url,
    title: typeof raw.title === 'string' ? raw.title : undefined,
    assetName: typeof raw.assetName === 'string' ? raw.assetName : undefined,
    assetType,
    thumbnailUrl: typeof raw.thumbnailUrl === 'string' ? raw.thumbnailUrl : null,
    duration: typeof raw.duration === 'number' ? raw.duration : null
  };
};

const normalizeForumThread = (thread: Partial<ForumThread>): ForumThread => ({
  id: thread.id || '',
  title: thread.title || '',
  body: thread.body || '',
  tag: thread.tag || 'question',
  authorId: thread.authorId || '',
  authorName: thread.authorName || 'Anonymous',
  authorPhotoURL: thread.authorPhotoURL ?? null,
  createdAt: thread.createdAt || new Date().toISOString(),
  updatedAt: thread.updatedAt || thread.createdAt || new Date().toISOString(),
  replyCount: Number(thread.replyCount) || 0,
  lastReplyAt: thread.lastReplyAt || thread.createdAt || new Date().toISOString(),
  score: Number(thread.score) || 0,
  upvotes: Number(thread.upvotes) || 0,
  downvotes: Number(thread.downvotes) || 0,
  sharedContent: normalizeForumSharedContent((thread as any).sharedContent)
});

const normalizeForumReply = (reply: Partial<ForumReply>): ForumReply => ({
  id: reply.id || '',
  threadId: reply.threadId || '',
  body: reply.body || '',
  authorId: reply.authorId || '',
  authorName: reply.authorName || 'Anonymous',
  authorPhotoURL: reply.authorPhotoURL ?? null,
  createdAt: reply.createdAt || new Date().toISOString(),
  updatedAt: reply.updatedAt || reply.createdAt || new Date().toISOString(),
  score: Number(reply.score) || 0,
  upvotes: Number(reply.upvotes) || 0,
  downvotes: Number(reply.downvotes) || 0
});

const toVoteMap = (raw: Record<string, unknown> | null | undefined): Record<string, ForumVote> => {
  if (!raw) return {};
  return Object.entries(raw).reduce<Record<string, ForumVote>>((acc, [id, value]) => {
    acc[id] = normalizeForumVote(value);
    return acc;
  }, {});
};

const getNextForumVote = (currentVote: ForumVote, direction: 1 | -1): ForumVote => {
  return currentVote === direction ? 0 : direction;
};

const applyVoteToEntity = (entity: any, previousVote: ForumVote, nextVote: ForumVote, now: string) => {
  if (!entity) return entity;
  const baseUpvotes = Number(entity.upvotes) || 0;
  const baseDownvotes = Number(entity.downvotes) || 0;
  const baseScore = Number(entity.score) || 0;

  const upvotes = Math.max(
    0,
    baseUpvotes + (previousVote === 1 ? -1 : 0) + (nextVote === 1 ? 1 : 0)
  );
  const downvotes = Math.max(
    0,
    baseDownvotes + (previousVote === -1 ? -1 : 0) + (nextVote === -1 ? 1 : 0)
  );
  const score = baseScore - previousVote + nextVote;

  return {
    ...entity,
    upvotes,
    downvotes,
    score,
    updatedAt: now
  };
};

export const listenToForumThreads = (
  callback: (threads: ForumThread[]) => void
): (() => void) => {
  const threadsQuery = query(
    dbRef(database, forumThreadPath),
    orderByChild('lastReplyAt'),
    limitToLast(100)
  );

  const unsubscribe = onValue(threadsQuery, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }
    const raw = snapshot.val() as Record<string, Partial<ForumThread>>;
    const threads = Object.values(raw)
      .filter((thread) => thread && thread.id)
      .map((thread) => normalizeForumThread(thread))
      .sort((a, b) => {
        const aTime = new Date(a.lastReplyAt || a.createdAt).getTime();
        const bTime = new Date(b.lastReplyAt || b.createdAt).getTime();
        return bTime - aTime;
      });
    callback(threads);
  });

  return () => unsubscribe();
};

export const listenToForumReplies = (
  threadId: string,
  callback: (replies: ForumReply[]) => void
): (() => void) => {
  const repliesQuery = query(
    dbRef(database, forumRepliesPath(threadId)),
    orderByChild('createdAt'),
    limitToLast(200)
  );

  const unsubscribe = onValue(repliesQuery, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }
    const raw = snapshot.val() as Record<string, Partial<ForumReply>>;
    const replies = Object.values(raw)
      .filter((reply) => reply && reply.id)
      .map((reply) => normalizeForumReply(reply))
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    callback(replies);
  });

  return () => unsubscribe();
};

export const listenToUserForumThreadVotes = (
  userId: string,
  callback: (votes: Record<string, ForumVote>) => void
): (() => void) => {
  const userVotesRef = dbRef(database, forumUserThreadVotesPath(userId));
  const unsubscribe = onValue(userVotesRef, (snapshot) => {
    const raw = snapshot.exists() ? (snapshot.val() as Record<string, unknown>) : null;
    callback(toVoteMap(raw));
  });
  return () => unsubscribe();
};

export const listenToUserForumReplyVotes = (
  userId: string,
  threadId: string,
  callback: (votes: Record<string, ForumVote>) => void
): (() => void) => {
  const userVotesRef = dbRef(database, forumUserReplyVotesPath(userId, threadId));
  const unsubscribe = onValue(userVotesRef, (snapshot) => {
    const raw = snapshot.exists() ? (snapshot.val() as Record<string, unknown>) : null;
    callback(toVoteMap(raw));
  });
  return () => unsubscribe();
};

export const createForumThread = async (params: {
  title: string;
  body: string;
  tag: 'question' | 'update' | 'feedback';
  authorId: string;
  authorName: string;
  authorPhotoURL?: string | null;
  sharedContent?: ForumSharedContent | null;
}): Promise<ForumThread> => {
  const now = new Date().toISOString();
  const newThreadRef = push(dbRef(database, forumThreadPath));
  const id = newThreadRef.key;

  if (!id) {
    throw new Error('Failed to create thread id');
  }

  const thread: ForumThread = {
    id,
    title: params.title.trim(),
    body: params.body.trim(),
    tag: params.tag,
    authorId: params.authorId,
    authorName: params.authorName,
    authorPhotoURL: params.authorPhotoURL ?? null,
    createdAt: now,
    updatedAt: now,
    replyCount: 0,
    lastReplyAt: now,
    score: 1,
    upvotes: 1,
    downvotes: 0,
    sharedContent: params.sharedContent ? { ...params.sharedContent } : null
  };

  const updates: Record<string, unknown> = {
    [`${forumThreadPath}/${id}`]: thread,
    [`${forumThreadVotesPath(id)}/${params.authorId}`]: 1,
    [`${forumUserThreadVotesPath(params.authorId)}/${id}`]: 1
  };

  await update(dbRef(database), updates);
  return thread;
};

export const createForumReply = async (params: {
  threadId: string;
  body: string;
  authorId: string;
  authorName: string;
  authorPhotoURL?: string | null;
}): Promise<ForumReply> => {
  const now = new Date().toISOString();
  const newReplyRef = push(dbRef(database, forumRepliesPath(params.threadId)));
  const id = newReplyRef.key;

  if (!id) {
    throw new Error('Failed to create reply id');
  }

  const reply: ForumReply = {
    id,
    threadId: params.threadId,
    body: params.body.trim(),
    authorId: params.authorId,
    authorName: params.authorName,
    authorPhotoURL: params.authorPhotoURL ?? null,
    createdAt: now,
    updatedAt: now,
    score: 1,
    upvotes: 1,
    downvotes: 0
  };

  const updates: Record<string, unknown> = {
    [`${forumRepliesPath(params.threadId)}/${id}`]: reply,
    [`${forumReplyVotesPath(params.threadId, id)}/${params.authorId}`]: 1,
    [`${forumUserReplyVotesPath(params.authorId, params.threadId)}/${id}`]: 1
  };

  await update(dbRef(database), updates);

  const threadRef = dbRef(database, `${forumThreadPath}/${params.threadId}`);
  try {
    await runTransaction(threadRef, (current) => {
      if (!current) return current;
      const nextReplyCount = (Number(current.replyCount) || 0) + 1;
      return {
        ...current,
        replyCount: nextReplyCount,
        lastReplyAt: now,
        updatedAt: now
      };
    });
  } catch (error) {
    console.warn('Unable to update thread reply count:', error);
  }

  return reply;
};

export const voteForumThread = async (params: {
  threadId: string;
  userId: string;
  direction: 1 | -1;
}): Promise<ForumVote> => {
  const voteRef = dbRef(database, `${forumThreadVotesPath(params.threadId)}/${params.userId}`);
  const userVoteRef = dbRef(database, `${forumUserThreadVotesPath(params.userId)}/${params.threadId}`);
  const currentVoteSnap = await get(userVoteRef);
  const currentVote = normalizeForumVote(currentVoteSnap.exists() ? currentVoteSnap.val() : 0);
  const nextVote = getNextForumVote(currentVote, params.direction);

  if (nextVote !== currentVote) {
    const now = new Date().toISOString();
    const threadRef = dbRef(database, `${forumThreadPath}/${params.threadId}`);
    await runTransaction(threadRef, (current) => applyVoteToEntity(current, currentVote, nextVote, now));
  }

  if (nextVote === 0) {
    await Promise.all([remove(voteRef), remove(userVoteRef)]);
  } else {
    await Promise.all([set(voteRef, nextVote), set(userVoteRef, nextVote)]);
  }

  return nextVote;
};

export const voteForumReply = async (params: {
  threadId: string;
  replyId: string;
  userId: string;
  direction: 1 | -1;
}): Promise<ForumVote> => {
  const voteRef = dbRef(
    database,
    `${forumReplyVotesPath(params.threadId, params.replyId)}/${params.userId}`
  );
  const userVoteRef = dbRef(
    database,
    `${forumUserReplyVotesPath(params.userId, params.threadId)}/${params.replyId}`
  );
  const currentVoteSnap = await get(userVoteRef);
  const currentVote = normalizeForumVote(currentVoteSnap.exists() ? currentVoteSnap.val() : 0);
  const nextVote = getNextForumVote(currentVote, params.direction);

  if (nextVote !== currentVote) {
    const now = new Date().toISOString();
    const replyRef = dbRef(database, `${forumRepliesPath(params.threadId)}/${params.replyId}`);
    await runTransaction(replyRef, (current) => applyVoteToEntity(current, currentVote, nextVote, now));
  }

  if (nextVote === 0) {
    await Promise.all([remove(voteRef), remove(userVoteRef)]);
  } else {
    await Promise.all([set(voteRef, nextVote), set(userVoteRef, nextVote)]);
  }

  return nextVote;
};

// Export User type for external use
export type { User };

// Get transcription from Firebase Database
export const getFirebaseTranscription = async (
  mediaId: string,
  userId?: string
): Promise<TranscriptionData | null> => {
  try {
    const sanitizedMediaId = sanitizeMediaId(mediaId);
    
    // Try user-specific data first, then public
    const paths = userId 
      ? [`users/${userId}/transcriptions/${sanitizedMediaId}`, `public/transcriptions/${sanitizedMediaId}`]
      : [`public/transcriptions/${sanitizedMediaId}`];

    for (const path of paths) {
      const transcriptionRef = dbRef(database, path);
      const snapshot = await get(transcriptionRef);
      
      if (snapshot.exists()) {
        console.log('Firebase transcription found at:', path);
        return snapshot.val() as TranscriptionData;
      }
    }
    
    console.log('No Firebase transcription found for:', sanitizedMediaId);
    return null;
  } catch (error) {
    console.error('Error getting Firebase transcription:', error);
    throw error;
  }
};

// Save transcription to Firebase Database
export const saveTranscription = async (
  mediaId: string,
  transcriptionData: TranscriptionData,
  userId?: string
): Promise<void> => {
  try {
    const sanitizedMediaId = sanitizeMediaId(mediaId);
    const timestamp = new Date().toISOString();
    const dataToSave = {
      ...transcriptionData,
      originalMediaId: mediaId, // Store original ID for reference
      firebase_createdAt: timestamp,
      firebase_updatedAt: timestamp
    };

    // Save under user's data if authenticated, otherwise in public space
    const path = userId 
      ? `users/${userId}/transcriptions/${sanitizedMediaId}`
      : `public/transcriptions/${sanitizedMediaId}`;
    
    const transcriptionRef = dbRef(database, path);
    await set(transcriptionRef, dataToSave);
    
    console.log('Transcription saved to Firebase:', path);
  } catch (error) {
    console.error('Error saving transcription:', error);
    throw error;
  }
};

// Trigger transcription generation via old API
export const triggerTranscription = async (
  mediaId: string,
  apiUrl: string = API_BASE_URL
): Promise<boolean> => {
  try {
    console.log('Triggering transcription for:', mediaId);
    
    const response = await fetch(`${apiUrl}/api/media/${mediaId}/transcribe`, {
      method: 'POST',
    });

    return response.ok;
  } catch (error) {
    console.error('Error triggering transcription:', error);
    return false;
  }
};

// Listen to transcription changes (real-time updates)
export const listenToFirebaseTranscription = (
  mediaId: string,
  callback: (transcription: TranscriptionData | null) => void,
  userId?: string
): (() => void) => {
  const sanitizedMediaId = sanitizeMediaId(mediaId);
  
  // Try user-specific data first, then public
  const paths = userId 
    ? [`users/${userId}/transcriptions/${sanitizedMediaId}`, `public/transcriptions/${sanitizedMediaId}`]
    : [`public/transcriptions/${sanitizedMediaId}`];

  const unsubscribeFunctions: (() => void)[] = [];
  let hasFoundData = false;

  // Listen to each possible path
  paths.forEach((path) => {
    const transcriptionRef = dbRef(database, path);
    
    const unsubscribe = onValue(transcriptionRef, (snapshot) => {
      if (snapshot.exists() && !hasFoundData) {
        hasFoundData = true;
        console.log('Firebase transcription listener update from:', path);
        callback(snapshot.val() as TranscriptionData);
      } else if (!snapshot.exists() && hasFoundData) {
        // Data was deleted
        hasFoundData = false;
        callback(null);
      }
    });
    
    unsubscribeFunctions.push(unsubscribe);
  });

  // Return a function that unsubscribes from all listeners
  return () => {
    unsubscribeFunctions.forEach(unsub => unsub());
  };
};

// Hook for using Firebase transcription in components
export const useFirebaseTranscription = (mediaId: string) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [transcription, setTranscription] = React.useState<TranscriptionData | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  // Listen to auth state changes
  React.useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
    });
    return unsubscribe;
  }, []);

  // Load existing transcription when mediaId or user changes
  React.useEffect(() => {
    const loadTranscription = async () => {
      if (!mediaId) return;
      
      setIsLoading(true);
      try {
        const transcriptionData = await getFirebaseTranscription(mediaId, user?.uid);
        setTranscription(transcriptionData);
      } catch (error) {
        console.error('Error loading Firebase transcription:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTranscription();
  }, [mediaId, user?.uid]);

  // Listen to real-time updates
  React.useEffect(() => {
    if (!mediaId) return;

    const unsubscribe = listenToFirebaseTranscription(
      mediaId,
      (transcriptionData) => {
        setTranscription(transcriptionData);
      },
      user?.uid
    );

    return unsubscribe;
  }, [mediaId, user?.uid]);

  return {
    transcription,
    isLoading,
    user
  };
};

// Hook for Firebase authentication
export const useFirebaseAuth = () => {
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(() => readPersistedAuthError());
  const [waitlisted, setWaitlisted] = React.useState<boolean>(() => readPersistedWaitlisted());

  // Listen to auth state changes
  React.useEffect(() => {
    let active = true;

    if (DEV_USER) {
      // Firebase is left alone entirely; only the backend session is claimed.
      syncBackendSession(DEV_USER)
        .catch((sessionError) => {
          console.warn('Dev session sync failed:', sessionError);
        })
        .finally(() => {
          if (!active) {
            return;
          }
          setUser(DEV_USER);
          setError(null);
          setIsLoading(false);
        });
      return () => {
        active = false;
      };
    }

    const unsubscribe = onAuthStateChange(async (nextAuthUser) => {
      let nextUser = nextAuthUser;
      let nextError = nextAuthUser ? null : readPersistedAuthError();
      let nextWaitlisted = nextAuthUser ? false : readPersistedWaitlisted();

      try {
        if (nextAuthUser) {
          persistAuthError(null);
          persistWaitlisted(false);
          await syncBackendSession(nextAuthUser);
          nextError = null;
        } else {
          await clearBackendSession();
          nextError = readPersistedAuthError();
          nextWaitlisted = readPersistedWaitlisted();
        }
      } catch (sessionError) {
        if (isAccountWaitlistedError(sessionError)) {
          nextUser = null;
          nextError = null;
          nextWaitlisted = true;
          persistWaitlisted(true);
          try {
            await signOutUser();
          } catch {}
          try {
            await clearBackendSession();
          } catch {}
        } else if (isBackendSessionAccessDeniedError(sessionError)) {
          nextUser = null;
          nextError = sessionError.message;
          persistAuthError(nextError);
          try {
            await signOutUser();
          } catch {}
          try {
            await clearBackendSession();
          } catch {}
        } else {
          console.warn('Backend session sync failed:', sessionError);
        }
      } finally {
        if (!active) {
          return;
        }
        setUser(nextUser);
        setError(nextError);
        setWaitlisted(nextWaitlisted);
        setIsLoading(false);
      }
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const completeSignIn = async (signInUser: () => Promise<User | null>, failureMessage: string) => {
    setIsLoading(true);
    persistAuthError(null);
    persistWaitlisted(false);
    setError(null);
    setWaitlisted(false);
    try {
      const user = await signInUser();
      if (user) {
        try {
          await syncBackendSession(user);
        } catch (sessionError) {
          if (isAccountWaitlistedError(sessionError)) {
            persistWaitlisted(true);
            try {
              await signOutUser();
            } catch {}
            try {
              await clearBackendSession();
            } catch {}
            setUser(null);
            setWaitlisted(true);
            return;
          }
          if (isBackendSessionAccessDeniedError(sessionError)) {
            persistAuthError(sessionError.message);
            try {
              await signOutUser();
            } catch {}
            try {
              await clearBackendSession();
            } catch {}
            setUser(null);
            setError(sessionError.message);
            return;
          }
          throw sessionError;
        }
        await saveUserProfile(user);
        setUser(user);
      }
    } catch (error: unknown) {
      setError(resolveErrorMessage(error, failureMessage));
      console.error('Sign-in failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = () => completeSignIn(signInWithGoogle, 'Failed to sign in with Google');

  const signInWithPassword = (email: string, password: string) =>
    completeSignIn(() => signInWithEmail(email, password), 'Failed to sign in');

  // Sign out
  const signOut = async () => {
    setIsLoading(true);
    persistAuthError(null);
    setError(null);
    try {
      await signOutUser();
      await clearBackendSession();
      setUser(null);
    } catch (error: unknown) {
      setError(resolveErrorMessage(error, 'Failed to sign out'));
      console.error('Sign-out failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const dismissWaitlist = React.useCallback(() => {
    persistWaitlisted(false);
    setWaitlisted(false);
  }, []);

  // A refused sign-in is shown once; closing it forgets it for the rest of the tab session.
  const dismissError = React.useCallback(() => {
    persistAuthError(null);
    setError(null);
  }, []);

  // Force refresh current user and update local state for UI
  const reloadUser = async () => {
    try {
      await auth.currentUser?.reload();
      setUser(auth.currentUser);
    } catch (e) {
      // no-op
    }
  };

  return {
    user,
    isLoading,
    error,
    waitlisted,
    dismissWaitlist,
    dismissError,
    signIn,
    signInWithPassword,
    signOut,
    isAuthenticated: user !== null,
    reloadUser
  };
};
